import React, { useState, useEffect, useTransition, useContext } from "react"
import { useTranslation } from "react-i18next"
import graphql from "babel-plugin-relay/macro"

import {
  useRefetchableFragment,
  useMutation
} from "react-relay"
import _ from "lodash"
import { Flex } from "theme-ui"
import {
  faPlus,
  faPen,
  faTimes,
  faEnvelope,
  faPhone,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CopyToClipboard } from "react-copy-to-clipboard"

import ReactTooltip from "../ReactTooltip/ReactTooltip"
import IconContainer from "../IconContainer/IconContainer"
import IntegrationIconDisplay from "../IntegrationIconDisplay/IntegrationIconDisplay"
import NewBoxButton from "../Radiate/NewBoxButton/NewBoxButton"
import Modal from "../../components/Radiate/Modal/Modal"
import IntegrationModal from "../../components/IntegrationModal/IntegrationModal"
import MemberEditModal from "../../components/MemberEditModal/MemberEditModal"
import { LoadingMask } from "../../CommonStyles"
import { UserContext } from "../../contexts/UserContext"

import * as Styles from "./MemberContainerStyles"

import {
  MemberContainerAddTagToMemberMutation,
  MemberContainerRemoveTagFromMemberMutation,
} from "./MemberContainerQuery"

import Note from "./Note"
import { randomString, isPhoneNumber } from "../util"
import { withTranslatedUtilOptions } from "../../HOC/withTranslatedUtilOptions"

const MemberContainer = ({
  threadId,
  withoutThreadId,
  onCloseMemberContainer,
  withCloseButton,
  i18nLocaleList,
  mobile,
  shouldHandleModalEvents = true, // Add prop to control modal handling
  views = [], // New prop for dynamic sections
  ...props
}) => {
  const { isSuperAdmin, isSuperAuditor } = useContext(UserContext)
  const { t } = useTranslation("common")
  const [
    data,
    refetch
  ] = useRefetchableFragment(
    graphql`
      fragment MemberContainer_userViewer on UserScope
      @argumentDefinitions(
        threadId: { type: "ID!" }
        withoutThreadId: { type: "Boolean!" }
      )
      @refetchable(queryName: "MemberContainerRefetchQuery") {
        configuration {
          member {
            tags
          }
        }
        thread (threadId: $threadId
        ) @skip(if: $withoutThreadId) {
          member {
            id
            groupChat
            wMemberId
            wChannelId
            wChannel {
              name
              integrationId
              integrationIcon
              integrationName
            }
            
            wAppId
            externalId
            externalProfile
            inboxProfile {
              firstName
              lastName
              website
              company
              profilePicUrl
              dob {
                date
                month
                year
              }
              gender
              locale
              phones {
                number
                alias
              }
              emails {
                email
                alias
              }
              addresses {
                alias
                line1
                line2
                city
                state
                country
                zip
              }
            }
            tags
            liveChat
            meta
            notes {
              _id
              content
              updatedAt
              authorId
              author {
                email {
                  email
                }
              }
              etag
            }
            etag
          }
        }
        role
        wUserId
      }
    `,
    props.data.userViewer
  )


  const member = data?.thread?.member || {}
  const isGroup = data?.thread?.member?.groupChat
  const [loading, startTransition] = useTransition()
  const [commitAddTag, isInFlightAddTag] = useMutation(MemberContainerAddTagToMemberMutation)
  const [commitRemoveTag, isInFlighRemoveTag] = useMutation(MemberContainerRemoveTagFromMemberMutation)
  const [tagError, setTagError] = useState("")
  const [openMemberEditModal, setOpenMemberEditModal] = useState(false)
  const [selectedGroupMember, setSelectedGroupMember] = useState(null) // Store selected group member data
  const [memberNotes, setMemberNotes] = useState(member?.notes || [])
  const [copyInfoText, setCopyInfoText] = useState(t?.("tooltips.copy"))
  const [openIntegrationModalUrl, setOpenIntegrationModalUrl] = useState(null)
  const [integrationModalView, setIntegrationModalView] = useState(null)
  const [modalWidth, setModalWidth] = useState(null)
  const [modalHeight, setModalHeight] = useState(null)
  const [modalInstanceId] = useState(() => Math.random().toString(36).substr(2, 9))

  // console.log({payload, contextToken, signedContext} , "Payload and Context");
  // console.log({views}, "views in MemberContainer");
  // console.log({member}, "Member Data in MemberContainer");
  
  // Function to transform group member data to MemberEditModal format
  const transformGroupMemberData = (groupMember) => {
    if (!groupMember) return null
    
    // console.log('Transforming group member data:', groupMember)
    
    // Transform group member data to match the expected MemberEditModal props
    const transformedInboxProfile = {
      firstName: groupMember.firstName || groupMember.name?.split(' ')[0] || '',
      lastName: groupMember.lastName || groupMember.name?.split(' ').slice(1).join(' ') || '',
      profilePicUrl: groupMember.profilePictureUrl || groupMember.avatar || '',
      phones: groupMember.externalId ? [{ number: groupMember.externalId, alias: 'primary' }] : [],
      emails: groupMember.email ? [{ email: groupMember.email, alias: 'primary' }] : [],
      addresses: [],
      locale: groupMember.locale || '',
      website: groupMember.website || '',
      company: groupMember.company || '',
      dob: groupMember.dob || {},
      gender: groupMember.gender || ''
    }
    
    const transformedExternalProfile = {
      name: groupMember.name || groupMember.externalId || 'Unknown',
      phone: groupMember.externalId,
      userName: groupMember.userName,
      firstName: groupMember.firstName,
      lastName: groupMember.lastName,
      ...groupMember.externalProfile
    }
    
    return {
      inboxProfile: transformedInboxProfile,
      externalProfile: transformedExternalProfile,
      wMemberId: groupMember.id || groupMember.wMemberId,
      externalId: groupMember.externalId || groupMember.id,
      etag: groupMember.etag || null
    }
  }
  
  const memberTagsOption = (_.get(data, "configuration.member.tags") || []).map(tag => ({
    value: tag,
    label: tag
  }))
 
  const {
    tags = [],
    notes = [],
    inboxProfile = {},
    externalProfile = {},
    externalId = "",
    wMemberId,
    etag,
    wChannel = {},
    wChannelId
  } = member

  const firstName = inboxProfile?.firstName || ""
  const lastName = inboxProfile?.lastName || ""
  const name = inboxProfile?.name || ""
  const profilePicUrl = inboxProfile?.profilePicUrl || ""
  const locale = inboxProfile?.locale || ""
  const phones = inboxProfile?.phones || []
  const emails = inboxProfile?.emails || []
  // const dob = profile?.dob || {}
  // const gender = profile?.gender || ""

  // console.log({views});
  
  
  useEffect(() => {
    setMemberNotes(notes || [])
  }, [JSON.stringify(notes)])

  // Set up dynamic sections configuration for groups
  const [groupSections, setGroupSections] = useState([])
  const [sectionStates, setSectionStates] = useState({}) // Track collapsed/expanded state for each section

  useEffect(() => {
    if (member?.wChannelId && views?.length > 0) {      
      // Add dynamic parameters to each section
      const sectionsWithParams = views?.map((section) => ({
        ...section,
        params: {
          groupId: member.externalId,
          wChannelId: member.wChannelId,
          sectionId: section.id, 
          contextToken: section?.contextToken, 
          ctx: section?.contextToken, 
          payload: JSON.stringify(section?.iframeContext?.payload ?? {}),
          pic: profilePicUrl ? encodeURIComponent(profilePicUrl) : "",
          signedContext: section?.iframeContext?.signedContext ?? "",
        },
      }))
      
      setGroupSections(sectionsWithParams)
      
      // Initialize section states (all expanded by default)
      const initialStates = {}
      sectionsWithParams.forEach(section => {
        initialStates[section.id] = { 
          isExpanded: true, 
          height: section.height || "400px" 
        }
      })
      setSectionStates(initialStates)
    }
  }, [isGroup, member?.wChannelId, member?.wChannel?.memberCount, member?.wChannel?.name, profilePicUrl])

  // Handle iframe messages
  useEffect(() => {
    
    const handleIframeMessage = (event) => {
      if (event?.data?.payload?.sectionId) {
        const sectionConfig = (groupSections || []).find(section =>
          section.id === event.data.payload.sectionId
        )

        if (sectionConfig) {
          const url = sectionConfig.url
          const viewOrigin = new URL(url).origin
          if (event.origin !== viewOrigin) {
            console.log("origin not whitelisted")
            return
          }
          if (event.data.type === "CLOSE_GROUP_MEMBERS") {
            // Handle close message from iframe if needed
            // console.log("Group members iframe closed")
          }

          // Handle integration action request from iframe using section ID
          if (event.data.type === "OPEN_INTEGRATION_MODAL" && shouldHandleModalEvents) {
            // console.log("Received OPEN_INTEGRATION_MODAL:", event.data.payload)

            let modalUrl = ""
            let modalWidth = null
            let modalHeight = null

            // Check if payload contains sectionId (new approach)
            if (event.data.payload.sectionId) {
              const sectionConfig = groupSections.find(section =>
                section.id === event.data.payload.sectionId
              )

              if (sectionConfig) {
                modalUrl = sectionConfig.url
                
                // Set modalHeight and modalWidth to empty strings for inbox_message_template
                if (event.data.payload.sectionId === "inbox_message_template") {
                  modalWidth = ""
                  modalHeight = ""
                } else {
                  modalWidth = sectionConfig.width
                  modalHeight = sectionConfig.height
                }
                
                // console.log("Found section config:", sectionConfig)
                setIntegrationModalView(sectionConfig)
              } else {
                console.warn("Section not found for ID:", event.data.payload.sectionId)
                return
              }
            } else {
              console.warn("No sectionId or modalView provided in payload")
              return
            }

            setModalWidth(modalWidth)
            setModalHeight(modalHeight)

            // Create URL with query parameters
            const url = new URL(modalUrl)

            // Create query object with all possible parameters
            const queryParams = {
              selectedContacts: event.data.payload.selectedContacts ? JSON.stringify(event.data.payload.selectedContacts) : null,
              groupInviteLink: event.data.payload.groupInviteLink,
              isGroupInvitation: event.data.payload.isGroupInvitation,
              context: event.data.payload.context,
              threadId: threadId,
              member: member?.id,
              payload: event.data.payload
            }

            // Add query parameters to URL, skipping null/undefined values
            Object.entries(queryParams).forEach(([key, value]) => {
              if (value !== null && value !== undefined) {
                url.searchParams.set(key, value)
              }
            })

            // console.log("Opening modal with URL:", url.toString())
            setOpenIntegrationModalUrl(url.toString())
          }

          // Handle section collapse/expand from iframe
          if (event.data.type === "SECTION_TOGGLE") {
            const { sectionId, isExpanded, height } = event.data.payload
            // console.log(`Section ${sectionId} toggled , ${isExpanded ? 'expanded' : 'collapsed'}`)

            // Find the section configuration to get the default height
            const sectionConfig = groupSections.find(s => s.id === sectionId)
            const sectionHeight = height || sectionConfig?.height || "400px"

            setSectionStates(prevStates => ({
              ...prevStates,
              [sectionId]: {
                isExpanded,
                height: sectionHeight
              }
            }))
          }

          // Handle successful group invitations sent
          if (event.data.type === "GROUP_INVITATIONS_SENT") {
            // const { success, message, sentCount } = event.data.payload
            // console.log("Group invitations sent:", { success, message, sentCount })
            setOpenIntegrationModalUrl(null)
          }
        }
      }

      // Handle member edit modal request from iframe
      if (event.data.type === "OPEN_MEMBER_EDIT_MODAL" && shouldHandleModalEvents) {
        // console.log("Received OPEN_MEMBER_EDIT_MODAL:", event.data.payload)
        const memberData = event.data.payload?.member
        setSelectedGroupMember(memberData) // Store the selected group member data
        setOpenMemberEditModal(true)
      }
    }

    window.addEventListener("message", handleIframeMessage)
    return () => {
      window.removeEventListener("message", handleIframeMessage)
    }
  }, [modalInstanceId, shouldHandleModalEvents, groupSections, setSectionStates])

  // Profile updates are handled automatically by Relay cache updates

  // Component unmount detection
  // useEffect(() => {
  //   return () => {
  //     console.log(`Component unmounting ${modalInstanceId}`)
  //   }
  // }, [modalInstanceId])

  const addTagFunction = (tag) => {
    let clientMutationId = 0
    let variables = {
      wMemberId: wMemberId,
      tag,
      clientMutationId: clientMutationId++,
    }
    const mutationParams = {
      variables: {
        input: variables,
      },
      onCompleted: (response, errs) => {
        if (!response.addTagToMember?.error) {
          
        }
      },
      onError: (err) => {
        const errorMsg = err.res?.json?.errors[0]?.message || err
        console.log("errorMsg", errorMsg)
        setTagError(errorMsg)
      }
    }
    commitAddTag(mutationParams)
  }

  const removeTagFunction = (tag) => {
    let clientMutationId = 0
    let variables = {
      wMemberId: wMemberId,
      tag,
      clientMutationId: clientMutationId++,
    }
    const mutationParams = {
      variables: {
        input: variables,
      },
      onCompleted: (response, errs) => {
        if (!response.removeTagFromMember?.error) {

        }
      },
      onError: (err) => {
        const errorMsg = err.res?.json?.errors[0]?.message || err
        console.log("errorMsg", errorMsg)
        setTagError(errorMsg)
      }
    }
    commitRemoveTag(mutationParams)
  }

  useEffect(() => {
    if (threadId) {
      startTransition(() => {
        refetch({
          threadId: threadId,
          withoutThreadId: !threadId
        }, {
          fetchPolicy: "network-only",
        })
      })
    }
  }, [threadId])

 

  const language = i18nLocaleList.find(o => o.value === locale)
  const tagsList = (tags || []).map((o) => {
    return ({
      label: o,
      value: o
    })
  })

  const generalStyles = {
    menu: styles => ({
      ...styles,
      width: "100%",
      color: "#626262",
      zIndex: 999,
      fontSize: "0.9rem"
    }),
    option: (styles, state) => ({
      ...styles,
      whitespace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ":hover": {
        ...styles[":hover"],
        overflow: "visible"
      }
    }),
    singleValue: styles => ({
      ...styles,
      color: "#626262",
      fontSize: "0.9rem"
    }),
    valueContainer: styles => ({
      ...styles,
      color: "#626262",
      fontSize: "0.9rem"
    })
  }

  if (loading) {
    return (
      <LoadingMask style={{ height: "100%", width: "100%", background: "#ffffff" }}>
        <div className="line"></div>
        <div className="line"></div>
        <div className="line"></div>
      </LoadingMask>
    )
  }

  const whatsappUsername = externalProfile?.username
  const isWhatsAppCloud = /^whatsapp-cloud/.test(wChannel?.integrationId)
  const phoneShared = isPhoneNumber(externalId)

  // phone number request handler
  const handleRequestPhoneNumber = () => {
    window.postMessage({
        type: "OPEN_INTEGRATION_MODAL",
        payload: {
          sectionId: "phone-number-request",
          externalId: externalId,
          channelId: wChannelId,
          view: "permittedRequestContactInfo",
        },
      }, "*")
  }

  const renderName = () => {
    if (firstName && lastName) {
      return (<Styles.TextM memberName>{firstName}&nbsp;{lastName}</Styles.TextM>)
    } else if (firstName) {
      return (<Styles.TextM memberName>{firstName}</Styles.TextM>)
    } else if (lastName) {
      return (<Styles.TextM memberName>{lastName}</Styles.TextM>)
    } else if (name) {
      return (<Styles.TextM memberName>{name}</Styles.TextM>)
    } else if (externalProfile?.firstName) {
      return (<Styles.TextM memberName>{externalProfile?.firstName}</Styles.TextM>)
    } else if (externalProfile?.lastName) {
      return (<Styles.TextM memberName>{externalProfile?.lastName}</Styles.TextM>)
    } else if (externalProfile?.name) {
      return (<Styles.TextM memberName>{externalProfile?.name}</Styles.TextM>)
    } else if (externalProfile?.username) {
      return (<Styles.TextM memberName>{externalProfile?.username}</Styles.TextM>)
    } 
    return (
      <>
        <Styles.TextM>{t?.("chat_message.user")}</Styles.TextM>
        <Styles.TextS memberName>{externalId}</Styles.TextS>
      </>
    )
  }

  return (
    <Styles.InnerMemberContainer>
      <Styles.MemberInfo>
        {withCloseButton &&
          <Styles.CloseMemberInfoButton
            icon={faTimes}
            borderless
            onClick={() => {
              onCloseMemberContainer()
            }}
          />
        }
        <Styles.EditButton
          borderless
          disabled={isSuperAdmin || isSuperAuditor}
          icon={faPen}
          onClick={() => {
            setSelectedGroupMember(null) // Clear any selected group member data
            setOpenMemberEditModal(true)
          }}
        />
        <IconContainer
          url={profilePicUrl}
          size="L"
          name={firstName || externalProfile?.name}
          showGroupPlaceholderIcon={isGroup}
        />
        {renderName()}
        {isWhatsAppCloud && !isGroup && whatsappUsername && (
          <Styles.TextS memberName>@{whatsappUsername}</Styles.TextS>
        )}
        <Styles.ChannelNameAndExternalIdWrapper>
          {!_.isEmpty(wChannel) && (
            <Styles.ChannelRow>
            </Styles.ChannelRow>
          )}
          {externalId && isWhatsAppCloud && (
            isGroup ? (
              <Styles.ChannelRow>
                <FontAwesomeIcon
                  style={{ marginRight: "4px", marginTop: "4px" }}
                  icon={faUserGroup}
                  size="20px"
                />
                <div style={{ wordBreak: "break-all" }} className="channel-name">{externalId}</div>
              </Styles.ChannelRow>
            ) : phoneShared ? (
              <Styles.ChannelRow>
                <FontAwesomeIcon
                  style={{ marginRight: "4px", marginTop: "4px" }}
                  icon={faPhone}
                  size="20px"
                />
                <div style={{ wordBreak: "break-all" }} className="channel-name">{externalId}</div>
              </Styles.ChannelRow>
            ) : (
              <Styles.PhoneHiddenContainer>
                <Styles.PhoneHiddenRow>
                  <FontAwesomeIcon
                    style={{ marginRight: "4px", marginTop: "4px" }}
                    icon={faPhone}
                    size="20px"
                  />
                  <Styles.PhoneHiddenBadge>{t?.("member_profile.phone_number_hidden")}</Styles.PhoneHiddenBadge>
                </Styles.PhoneHiddenRow>
                <Styles.RequestPhoneButton
                  text={t?.("member_profile.request_phone_number")}
                  primary
                  borderless
                  onClick={handleRequestPhoneNumber}
                />
              </Styles.PhoneHiddenContainer>
            )
          )}
        </Styles.ChannelNameAndExternalIdWrapper>
        {language?.text && (
          <Styles.TextS className="member-locale">{language?.text}</Styles.TextS>
        )}
        {/* {(!_.isEmpty(dob) && dob?.year && dob?.month && dob?.date) && (
          <Flex>
            <Styles.TextS>Date of Birth | {`${dob.date}-${moment().month(dob.month).format("MMM")}-${dob.year}`}</Styles.TextS>
          </Flex>
          
        )}
        {gender && (
          <Styles.TextS>{gender.charAt(0).toUpperCase() + gender.slice(1)}</Styles.TextS>
        )} */}
        <div className="member-email">
          {(emails || []).map((email) => {
            return (
              <CopyToClipboard
                text={email?.email}
                onCopy={() => {
                  setCopyInfoText(t?.("tooltips.copied"))
                  setTimeout(() => {
                    setCopyInfoText(t?.("tooltips.copy"))
                  }, 1000)
                }}
              >
                <Styles.EmailRow
                  data-tooltip-content={copyInfoText}
                  data-tooltip-id="member-info-copy-tooltip"
                >
                  <FontAwesomeIcon
                    className="icon"
                    icon={faEnvelope}
                  />
                  <div className="email">{email?.email}</div>
                </Styles.EmailRow>
              </CopyToClipboard>
            )
          })}
        </div>
        {!/^whatsapp-cloud/.test(wChannel?.integrationId) && (
          <div className="member-phone">
            {(phones || []).map((phone) => {
              return (
                <CopyToClipboard
                  text={phone?.number}
                  onCopy={() => {
                    setCopyInfoText(t?.("tooltips.copied"))
                    setTimeout(() => {
                      setCopyInfoText(t?.("tooltips.copy"))
                    }, 1000)
                  }}
                >
                  <Styles.EmailRow
                    data-tooltip-content={copyInfoText}
                    data-tooltip-id="member-info-copy-tooltip"
                  >
                    <FontAwesomeIcon
                      className="icon"
                      icon={faPhone}
                    />
                    <div className="email">{phone?.number}</div>
                  </Styles.EmailRow>
                </CopyToClipboard>
              )
            })}
          </div>
        )}
      </Styles.MemberInfo>
      <ReactTooltip
        id="member-info-copy-tooltip"
        place="left"
      />
      {!_.isEmpty(wChannel) && (
        <Styles.ChannelSection>
          <IntegrationIconDisplay
            icon={wChannel?.integrationIcon}
            name={wChannel?.integrationName}
            size="20px"
          />
          <div className="channel-name">{wChannel?.name}</div>
        </Styles.ChannelSection>
      )}
      <Styles.TagsContainer>
        <div className="title-container">
          <div className="title">{t?.("member_profile.tags")}</div>
          <div className="error">{tagError}</div>
        </div>
        <Styles.StyledCreatable
          options={memberTagsOption}
          placeholder={t?.("member_profile.create_tag_placeholder")}
          styles={generalStyles}
          isMulti
          isDisabled={isInFlightAddTag || isInFlighRemoveTag || isSuperAdmin || isSuperAuditor}
          isClearable={false}
          value={tagsList}
          onChange={(data) => {
            const oldTagsList = member?.tags ?? []
            let newTagsList
            if (!data?.length) {
              newTagsList = []
            } else {
              newTagsList = data.map(o => o.value)
            }
            const addedTag = _.difference(newTagsList, oldTagsList)
            const removedTag = _.difference(oldTagsList, newTagsList)
            if (addedTag?.length) {
              addTagFunction(addedTag[0])
            } else if (removedTag?.length) {
              removeTagFunction(removedTag[0])
            }
          }}
        />
      </Styles.TagsContainer>
      {/* Note Section */}
      <Styles.NotesContainer>
        <Flex
          className="title-container"
          sx={{ alignItems: "center" }}
        >
          <div className="title">{t?.("notes.notes")}</div>
          {(!isSuperAdmin && !isSuperAuditor) &&
            <NewBoxButton
              icon={faPlus}
              text={t?.("notes.note")}
              primary
              borderless
              disabled={(memberNotes || []).find(n => !n._id)}
              onClick={() => {
                const newNotes = [
                  ...memberNotes,
                  {
                    noteId: randomString(8),
                    content: ""
                  }
                ]
                setMemberNotes(newNotes)
              }}
            />
          }
        </Flex>
        {(memberNotes || []).map((note) => {
          return (
            <Note
              key={note?.noteId || note?._id}
              noteInfo={note}
              wMemberId={wMemberId}
              ownUserId={data?.wUserId}
              onCancelUnsavedNote={(noteId) => {
                const newMemberNotes = memberNotes.filter(o => o.noteId !== noteId)
                setMemberNotes(newMemberNotes)
              }}
            />
          )
        })}
      </Styles.NotesContainer>
      {/* Dynamic Sections for Groups */}
      {groupSections.length > 0 && groupSections
        .filter(section => {
          // Show all sections with MEMBER_SIDEBAR placement
          if (section.placements && section.placements.includes("MEMBER_SIDEBAR")) {
            return true
          }
          // Only show GROUP_MEMBER_SIDEBAR sections if it's a group
          if (section.placements && section.placements.includes("GROUP_MEMBER_SIDEBAR")) {
            return isGroup
          }
          return false
        })
        .map((section) => {
        const sectionUrl = section.params ? 
          `${section.url}?${new URLSearchParams(section.params).toString()}` : 
          section.url
        
        // Get the current state of this section (default to expanded)
        const sectionState = sectionStates[section.id] || { isExpanded: true, height: section.height || "400px" }
        
        // Determine height based on collapsed/expanded state
        const iframeHeight = sectionState.isExpanded 
          ? (sectionState.height || section.height || "400px")
          : "50px"
        
        // Container height should match iframe height
        const containerHeight = iframeHeight
          
        return (
          <Styles.MembersContainer 
            key={section.id} 
            style={{ 
              height: containerHeight,
              minHeight: containerHeight,
              maxHeight: containerHeight,
              overflow: 'hidden',
              transition: 'height 0.3s ease'
            }}
          >
            <Styles.MembersContent style={{ height: '100%' }}>
              <Styles.GroupMembersIframe
                src={sectionUrl}
                title={section.title}
                frameBorder="0"
                width="100%"
                height={iframeHeight}
                allow="clipboard-read; clipboard-write"
                onLoad={(e) => {
                  // console.log(`${section.title} iframe loaded`)
                  // Send profilePicUrl via postMessage after iframe loads
                  if (profilePicUrl && e.target.contentWindow) {
                    e.target.contentWindow.postMessage({
                      type: "PROFILE_PIC_URL",
                      payload: {
                        profilePicUrl: profilePicUrl,
                        sectionId: section.id
                      }
                    }, "*")
                  }
                }}
              />
            </Styles.MembersContent>
          </Styles.MembersContainer>
        )
      })}
      {/* {isGroup && groupSections.length > 0 && (
        <Styles.DeleteGroupButton>
          <FontAwesomeIcon
            className="delete-icon"
            icon={faTrash}
          />
          <span>{t?.("member_profile.delete_group") || "Delete group"}</span>
        </Styles.DeleteGroupButton>
      )} */}
      <Modal
        open={openMemberEditModal}
      >
        {({ modalRef }) => {
          // Use group member data if available, otherwise use regular member data
          const memberDataToUse = selectedGroupMember 
            ? transformGroupMemberData(selectedGroupMember)
            : {
                inboxProfile,
                externalProfile,
                wMemberId,
                externalId,
                etag,
                groupId: member?.externalId,
              }
          
          return (
            <MemberEditModal
              ref={modalRef}
              groupId={member?.externalId}
              inboxProfile={memberDataToUse.inboxProfile}
              profile={memberDataToUse.externalProfile}
              wChannel={wChannel}
              etag={memberDataToUse.etag}
              wMemberId={memberDataToUse.wMemberId}
              externalId={memberDataToUse.externalId}
              wChannelId={wChannelId}
              mobile={mobile}
              onClose={() => {
                setOpenMemberEditModal(false)
                setSelectedGroupMember(null) // Clear selected group member when closing
              }}
            />
          )
        }}
      </Modal>
      {/* 16 July */}

      {/* Integration Modal for Add Members 16 july (To Watch The Modal Event) */}
      <Modal
        key={openIntegrationModalUrl ? `modal-${openIntegrationModalUrl}` : 'modal-closed'}
        open={!!openIntegrationModalUrl}
      >
        {() => {
          return (
            <IntegrationModal
              url={openIntegrationModalUrl}
              modalWidth={modalWidth}
              modalHeight={modalHeight}
              externalId={externalId}
              isGroup={isGroup}
              contextToken={integrationModalView?.contextToken}
              iframeContext={{
                payload: integrationModalView?.iframeContext?.payload,
                signedContext: integrationModalView?.iframeContext?.signedContext,
              }}
              onClose={() => {
                // console.log(`Modal onClose called ${modalInstanceId}`)
                setOpenIntegrationModalUrl(null)
                setModalWidth(null)
                setModalHeight(null)
              }}
            />
          )
        }}
      </Modal>
      {/* 16 July */}
    </Styles.InnerMemberContainer>
  )
}

export default withTranslatedUtilOptions(MemberContainer)


